<template :key="componentKey">
  <div>
    <div class="row mb-3">
      <div class="col-8">
        <h2 class="h3 mb-2">{{ __('label:create-plan title') }}</h2>
        <h5 v-if="plan && plan.years && plan.years.length"><i class="far fa-calendar-alt"></i> Duration: {{ plan.years.length }}
          {{ plan.years.length > 1 ? 'years' : 'year' }}</h5>
        <p>View your Course Plan below. This shows the shortest possible schedule for completing the units you selected.</p>
      </div>
      <div class="col-4 text-right print-hide">
        <button class="btn-icon-round btn-solid-dark btn-lg btn-hover-expand" v-on:click="printScreen"><i class="far fa-print"></i><span
            class="text">Print / Save as PDF</span></button><br />
        <button class="btn-icon-round btn-solid-dark btn-lg btn-hover-expand mt-1" v-on:click="exportExcel"><i class="far fa-file-excel"></i><span
            class="text">Export to Excel</span></button>
        <br />
        <button class="btn-icon-round btn-solid-dark btn-lg btn-hover-expand mt-1" v-if="!noUnitsAllocated" v-b-modal.share-plan-modal
          id="share-plan-modal-button"><i class="fa fa-link"></i><span
            class="text">Share plan</span></button>
        <br />
        <button v-if="!noUnitsAllocated" class="btn btn-primary mt-3" id="tooltip-regenerate-btn" v-on:click="Generate">Regenerate course plan</button>
      </div>
      <b-tooltip target="tooltip-regenerate-btn" triggers="hover" placement="bottom">
        Regenerating your course plan will shuffle the below units to provide another possible course plan. You can 'Lock' specific units in place below before
        regenerating.
      </b-tooltip>
      <b-tooltip target="share-plan-modal" triggers="hover" placement="bottom">
        Save your course plan and share it via a link or email.
      </b-tooltip>
      <b-modal id="share-plan-modal" size="lg" hide-header hide-footer no-close-on-backdrop>
        <div class="modal-close-head align-items-center mb-3">
          <h3 class="mb-0">Share your course plan</h3>
          <button type="button" v-on:click="$bvModal.hide('share-plan-modal')" class="modal-close btn-clear"><i class="far fa-times"></i></button>
        </div>
        <share-plan-modal plan="plan" :academicChair="academicChair"></share-plan-modal>
      </b-modal>
    </div>

    <div class="row">
      <div class="col">
        <div class="row" v-if="loading">
          <div class="page-content-state page-content-state-loading col-12">
            <span class="page-content-state__icon  far fa-spinner-third fa-spin"></span>
            <div class="page-content-state__content">Generating course plan</div>
          </div>
        </div>
        <div class="row" v-else-if="error">
          <div class="page-content-state col-12">
            <span class="page-content-state__icon fal fa-sad-cry"></span>
            <div class="page-content-state__header">Sorry, there was a problem generating your course plan.</div>
            <button class="btn btn-primary mt-3" id="tooltip-regenerate-btn" v-on:click="Generate">Try again</button>
          </div>
        </div>

        <div v-if="plan && !loading && !error" class="cplan">

          <!-- Banner alerts / messages -->
          <div v-if="isInternationalPartTime" class="page-alert warning">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div class="page-alert__body">
              In most circumstances international student visa holders need to study full time (4 units or 12 points per semester/trimester).
            </div>
          </div>

          <div class="page-alert warning" v-if="exclusionUnitConflicts.length">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div>
              <div class="page-alert__title">{{ exclusionUnitConflicts.length }} unit exclusion conflict{{exclusionUnitConflicts.length>0?'s':''}}</div>
              <div class="page-alert__body">
                <p>Please see the <a href="#" v-on:click="scrollToConflictingUnits">Conflicting units section</a> below for more details</p>
              </div>
            </div>
          </div>

          <div class="page-alert warning" v-if="unallocatedUnits && unallocatedUnits.length && !noUnitsAllocated">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div>
              <div class="page-alert__title" v-if="unallocatedUnits && unallocatedUnits.length < 2">{{ unallocatedUnits.length }} Unallocated unit</div>
              <div class="page-alert__title" v-if="unallocatedUnits && unallocatedUnits.length > 1">{{ unallocatedUnits.length }} Unallocated units</div>
              <div class="page-alert__body">
                <p>Please see the <a href="#" v-on:click="scrollToUnallocated">Unallocated units section</a> below for more details</p>
              </div>
            </div>
          </div>

          <div class="page-alert warning" v-else-if="noUnitsAllocated">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div>
              <div class="page-alert__title">The system failed to schedule any of your units</div>
              <div class="page-alert__body">
                <p>Please try to regenerate your plan to get a better result.<br />
                  If the problem persists you may need to contact your academic chair to help resolve it.</p>
              </div>
            </div>
          </div>

          <!-- Completed units... -->
          <div class="cplan-completed" v-if="completedUnits && completedUnits.length > 0">
            <div class="cplan-completed__head">
              <div class="cplan-completed__title">
                <i class="far fa-check-circle"></i> Units you've already completed
              </div>
              <div class="cplan-completed__credits">
                {{completedTotalCredits}} points completed
              </div>
            </div>
            <div class="card-row">
              <CompletedUnitCard v-for="unitCode in completedUnits" v-bind:key="unitCode"
                :unitCode="unitCode" />
            </div>
          </div>

          <!-- Scheduled units... -->
          <template v-for="(year, index) in plan.years">
            <div class="cplan-year" :key="year.teachingPeriod">
              <div class="cplan-year__head">
                <span class="cplan-year__head-num">Year {{ index + 1 }}</span>
                <span class="cplan-year__head-year">{{ startYear + index  }}</span>
              </div>
              <template v-for="teachingPeriod in year.teachingPeriods">
                <TeachingPeriod :key="teachingPeriod.teachingScheduled" :teachingPeriod="teachingPeriod"
                  :teachingPeriodTitle="getTeachingPeriodLabel(teachingPeriod.teachingScheduled)" :year="index + 1" :startYear="startYear"
                  @displayGenerateError="(errorVisible)=>displayGenerateError=errorVisible" />
                <transition name="pop" :key="teachingPeriod.teachingScheduled">
                  <div class="unit-card__alert-bottom prereq" v-if="displayGenerateError">
                    <span class="unit-card__alert-bottom-content" :id="`tooltip-prereq-alert-${index + 1 + teachingPeriod.teachingScheduled}`">
                      Error generating plan
                    </span>
                  </div>
                </transition>
              </template>
            </div>
          </template>

          <!-- Exclusion conflicted units -->
          <div v-if="exclusionUnitConflicts.length" class="cplan-unalloc" id="anchor-conflicts" ref="anchorConflicts">
            <div class="row mt-5">
              <div class="col-12">
                <h4>You have {{ exclusionUnitConflicts.length }} unit{{exclusionUnitConflicts.length>0?'s':''}} with an exclusion conflict</h4>
                <p>The following unit(s) have exclusion conflicts. Please click on the units below to identify the conflict then go back to 'Select Your Units' to unselect one or more units before proceeding. For more information please contact your Academic Chair below.</p>
                <div class="card-row">
                  <template v-for="(unit, index) in exclusionUnitConflicts">
                    <UnallocUnitCard :key="index" :unit="unit" />
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Unallocated units... -->
          <div v-if="unallocatedUnits && unallocatedUnits.length" class="cplan-unalloc" id="anchor-unallocated" ref="anchorUnallocated">
            <div class="row mt-5">
              <div class="col-12">
                <h4 v-if="unallocatedUnits.length == 1">You have {{ unallocatedUnits.length }} unallocated unit</h4>
                <h4 v-if="unallocatedUnits.length > 1">You have {{ unallocatedUnits.length }} unallocated units</h4>
                <div v-if="unallocatedOfferings.length > 0" class="mt-3">
                  <h5>Unit(s) cannot be scheduled</h5>
                  <div class="row">
                    <div class="col-12 col-lg-9">
                      <p>The following unit(s) cannot be scheduled because there are no valid offerings within the timeframe or the unit(s) have teaching
                        periods
                        which span over the full year. Please contact your Academic Chair below.</p>
                    </div>
                  </div>
                  <div class="card-row">
                    <template v-for="(unit, index) in unallocatedOfferings">
                      <UnallocUnitCard :key="index" :unit="unit" />
                    </template>
                  </div>
                </div>
                <div v-if="unallocatedPreReqs.length > 0" class="mt-3">
                  <h5>Missing pre-requisite</h5>
                  <div class="row">
                    <div class="col-12 col-lg-9">
                      <p>The following unit(s) need pre-requisite unit(s). Please click on the unit(s) below to identify the pre-requisite unit(s), go back to
                        'Select Your Units' and add them to your selection.</p>
                    </div>
                  </div>
                  <div class="card-row">
                    <template v-for="(unit, index) in unallocatedPreReqs">
                      <UnallocUnitCard :key="index" :unit="unit" />
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- <div v-if="plan.years.length > 99">THERE WAS A PROBLEM</div> -->

          <div class="highlight-section mt-5" v-if="!noUnitsAllocated && academicChair.length > 0">
            <span class="highlight-section__icon-outer">
              <i class="highlight-section__icon fas fa-thumbs-up"></i>
            </span>
            <div class="row">
              <div class="col-12">
                <h4 class="highlight-section__head">Next steps...</h4>
                <p class="highlight-section__intro-text">Now that you have generated a course plan your next step is to get it approved by the relevant Academic
                  Chair.
                </p>
                <p>If you are having trouble scheduling a unit or the plan is not quite how you want it your Academic Chair can assist if you email the plan to
                  your Academic Chair by clicking Share Plan.</p>
                <p>Once the plan has been approved you can begin enrolling in units for your course.</p>
              </div>

              <div class="row mt-4 ml-0 mr-2 d-flex justify-content-center">
                <div class="col-12 mb-3">
                  <h4 class="mb-2">Academic Chair</h4>
                  <div v-for="chair in academicChair" v-bind:key="chair.index">
                    <h5 class="mb-1">
                      {{ chair.name }}: <a class="academic-chair__email" :href="`mailto:${chair.email}`">{{ chair.email }}</a>
                    </h5>
                    <div v-if="!chair.appliesToAllOfferings" class="academic-chair">
                      <h5 class="mb-1">Major: <span>{{ chair.title }}</span></h5>
                      <h5 class="mb-1">College: <span>{{ chair.owningCollege }}</span></h5>
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <h4 id="student-notes-head">Student notes</h4>
                  <p>Add any notes that your wish to share with the Academic Chair regarding your Course Plan.</p>
                  <div class="form-group pb-0 mb-0">
                    <textarea class="form-control" id="student-notes" v-model="studentNotes" style="min-height: 72px;" aria-labelledby="student-notes-head"
                      ref="studentNoteTextarea"
                      v-on:keyup="textAreaSize"></textarea>
                  </div>
                </div>
                <div class="col-12 print-hide">
                  <button hidden class="btn btn-primary my-3" v-b-modal.share-plan-academic-chair-modal id="share-plan-academic-chair-modal-button">
                    <i class="fa fa-link"></i> Share with Academic Chair
                  </button>
                  <button class="btn btn-secondary ml-1 my-3" v-on:click="printScreen" id="share-plan-academic-chair-modal-button">
                    <i class="far fa-print"></i> Print / Save as PDF
                  </button>

                  <button class="btn btn-primary my-3" v-b-modal.share-plan-modal id="share-plan-modal-button">
                    <i class="far fa-link"></i> Share plan
                  </button>
                </div>
              </div>

            </div>
          </div>

          <div class="row mt-5" v-if="courseDetails">
            <div class="col-12 col-lg-6">
              <h4>Course details</h4>
              <p v-html="courseDetails"></p>
            </div>
          </div>

          <b-modal hidden id="share-plan-academic-chair-modal" size="lg" hide-header hide-footer no-close-on-backdrop>
            <div class="modal-close-head align-items-center mb-4">
              <h3 class="mb-0">Share with your Academic Chair</h3>
              <button type="button" v-on:click="$bvModal.hide('share-plan-academic-chair-modal')" class="modal-close btn-clear"><i
                  class="far fa-times"></i></button>
            </div>
            <share-plan-academic-chair-modal plan="plan" :selectedMajors="courseMajors" :academicChair="academicChair">
            </share-plan-academic-chair-modal>
          </b-modal>

          <div class="mt-5"><strong>This Course Plan was generated on {{ getDateTime }} AWST.</strong></div>

          <div class="mt-5 print-only">
            <h6>Disclaimer</h6>
            <ul>
              <li>The Course Visualiser assists with the generation of a Course Plan. It should not be enacted until it is validated with the Academic Chair
                of
                the Course.</li>
              <li>This Course Plan is based on the course structure and offerings of latest Approved Courses only. It is the responsibility of students to
                ensure the correct availability of units in each semester/trimester of each academic year.</li>
              <li>Please contact the Academic Chair of the Course if you have any queries relating to the Course Plan.</li>
            </ul>
          </div>

        </div>

      </div>
    </div>

  </div>
</template>

<style scoped lang="scss"></style>

<script>
import useBaseStep from 'client/js/composition/base-step'
import inputBuilder from 'common/helpers/input-builder.js'
import preReqValidator from 'common/helpers/pre-req-validator.js'
import exclusionValidator from 'common/helpers/exclusion-validator.js'
import StudentPreferences from 'client/js/models/student-preferences.js'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import moment from 'moment'
import sortUnitsByTitle from 'common/helpers/sort-units-by-title.js'

import Banner from './common/Banner.vue'
import TeachingPeriod from './create-plan/TeachingPeriod.vue'
import CompletedUnitCard from './create-plan/CompletedUnitCard.vue'
import UnallocUnitCard from './create-plan/UnallocUnitCard.vue'
import Curriculum from 'client/js/models/curriculum.js'
import XLSX from 'xlsx'
import SharePlanModal from './SharePlanModal.vue'
import SharePlanAcademicChairModal from './SharePlanAcademicChairModal.vue'

export default {
  components: { Banner, TeachingPeriod, CompletedUnitCard, UnallocUnitCard, SharePlanModal, SharePlanAcademicChairModal },
  setup(props, context) {
    return {
      baseStep: useBaseStep(props, context)
    }
  },
  data() {
    return {
      academicChair: [],
      error: null,
      componentKey: 0,
      loading: false,
      displayGenerateError: false,
      savedPlanId: null
    }
  },
  computed: {
    ...mapGetters({
      course: 'courses/getSelectedCourse',

      teachingPeriods: 'preferences/getTeachingPeriods',
      selectedPreferences: 'preferences/getSelectedStudyPreferences',
      isInternationalPartTime: 'preferences/isInternationalPartTime',

      selectedItems: 'units/getSelectedCurriculumItems',
      completedUnits: 'units/getCompletedUnits',
      selectedUnitCodes: 'units/getSelectedUnitCodes',
      getStudentNotes: 'plans/getStudentNotes',
      plan: 'plans/getPlanData'
    }),
    studentNotes: {
      get() {
        return this.getStudentNotes
      },
      set(value) {
        this.setStudentNotes(value)
      }
    },
    courseMajors() {
      return this.selectedItems.filter((item) => item.relationship.type === 'Major')
    },
    courseDetails() {
      return this.course?.caveats?.length ? this.course.caveats[0].description : null
    },
    startYear() {
      return parseInt(this.selectedPreferences.startYear)
    },
    allocatedUnitIds() {
      if (this.plan == null) {
        return []
      }
      return this.plan.years.reduce((arr, year) => {
        const units = year.teachingPeriods.reduce((periodUnits, tp) => {
          periodUnits = [...periodUnits, ...tp.units.map((u) => u.id)]
          return periodUnits
        }, [])
        arr = [...arr, ...units]
        return arr
      }, [])
    },
    exclusionUnitConflicts() {
      const conflicts = this.selectedItems.filter(
        (item) => item.relationship.unit?.requisites &&
          (
            exclusionValidator.exclusionClashTest(item.relationship.code, this.selectedUnitCodes, inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Exclusion') || [])
            || exclusionValidator.exclusionClashTest(item.relationship.code, this.selectedUnitCodes, inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Equivalent') || [])
          )
      )
      // console.log('Exclusions', conflicts.map(item => [item.relationship?.code, inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Exclusion')]))
      // Remove duplicates from conficts
      return conflicts?.filter((value, index, self) => self.indexOf(value) === index) || []
    },
    unallocatedUnits() {
      return this.selectedItems
        .filter(
          (item) =>
            item.relationship.type === 'Unit' &&
            this.allocatedUnitIds.indexOf(item.relationship.id) < 0 &&
            !this.completedUnits.includes(item.relationship.code)
        )
        .sort(sortUnitsByTitle)
        .reduce((total, current) => {
          // Unique based on relationship code
          if (!total.map((x) => x.relationship.code).includes(current.relationship.code)) {
            total.push(current)
          }
          return total
        }, [])
    },
    unallocatedOfferings() {
      return this.unallocatedUnits.filter(
        (item) =>
          item.relationship.unit == null ||
          item.relationship.unit.requisites == null ||
          preReqValidator.prerequisitesExistTest(
            item.relationship.code,
            this.selectedUnitCodes,
            inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Prerequisite')
          )
      )
    },
    unallocatedPreReqs() {
      const uniqueItems = this.unallocatedUnits.filter(
        (item) =>
          item.relationship.unit == null ||
          item.relationship.unit.requisites == null ||
          !preReqValidator.prerequisitesExistTest(
            item.relationship.code,
            this.selectedUnitCodes,
            inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Prerequisite')
          )
      )
      return uniqueItems
    },
    noUnitsAllocated() {
      if (!this.loading && this.allocatedUnitIds.length == 0 && this.unallocatedUnits.length > 0) {
        return true
      }
      return false
    },
    validLocations() {
      return StudentPreferences.getCourseLocations(this.course, this.selectedPreferences.studentType, this.selectedPreferences.placeOfResidence)
    },
    defaultWorkload() {
      return StudentPreferences.courseloopOptionToStudyOption(this.selectedPreferences.studyOption).workload
    },
    startTeachingScheduled() {
      return [
        {
          year: 1,
          teachingSchedule: StudentPreferences.getStartTeachingSchedule(this.selectedPreferences.teachingPeriod, this.selectedPreferences.summerWinter)
        }
      ]
    },
    defaultTeachingScheduled() {
      return StudentPreferences.getDefaultTeachingScheduled(this.selectedPreferences.teachingPeriod, this.selectedPreferences.summerWinter)
    },
    missingPreReqSelectedItems() {
      return this.selectedItems.filter(
        (item) =>
          item.relationship.unit == null ||
          item.relationship.unit.requisites == null ||
          !preReqValidator.prerequisitesExistTest(
            item.relationship.code,
            this.selectedUnitCodes,
            inputBuilder.convertRequisites(item.relationship.unit.requisites, 'Prerequisite')
          )
      )
    },
    completedSelectedItems() {
      return this.selectedItems.filter((item) => this.completedUnits.includes(item.code))
    },
    completedTotalCredits() {
      return this.completedSelectedItems
        // Filter out duplicates based on unit code
        .filter((item, index, self) => self.findIndex(t => t.code === item.code) === index)
        // Sum the credits of the completed units
        .reduce((total, item) => total + (parseInt(item.relationship.credits) || 0), 0)
    },
    getDateTime: function () {
      return moment().utcOffset(8).format('DD/MM/YY, h:mm a')
    }
  },
  methods: {
    ...mapActions({
      generateCoursePlan: 'plans/generateCoursePlan'
    }),
    ...mapMutations({
      setStudentNotes: 'plans/setStudentNotes'
    }),
    getTeachingPeriodLabel(code) {
      const teachingPeriod = StudentPreferences.defaultTeachingPeriods.find((tp) => tp.code === code)
      if (teachingPeriod) {
        return teachingPeriod.label
      }
      return code
    },
    textAreaSize() {
      this.$refs.studentNoteTextarea.style.minHeight = '0'
      this.$refs.studentNoteTextarea.style.height = '1px'
      if (this.$refs.studentNoteTextarea.scrollHeight + 25 > 72) {
        this.$refs.studentNoteTextarea.style.minHeight = this.$refs.studentNoteTextarea.scrollHeight + 25 + 'px'
      } else {
        this.$refs.studentNoteTextarea.style.minHeight = '72px'
      }
    },
    scrollToUnallocated() {
      var unallocPosition = this.$refs.anchorUnallocated.getBoundingClientRect().y
      var offsetMargin = 30
      document.getElementById('wrapperOuter').scrollTop = unallocPosition - offsetMargin
    },
    scrollToConflictingUnits() {
      var conflictPosition = this.$refs.anchorConflicts.getBoundingClientRect().y
      var offsetMargin = 30
      document.getElementById('wrapperOuter').scrollTop = conflictPosition - offsetMargin
    },
    printScreen() {
      window.print()

      if (this.$gtm.enabled())
        this.$gtm.trackEvent({
          event: 'site interaction',
          category: 'Course Plan and Fees',
          action: 'click',
          label: 'Print',
          value: true,
          rest: {
            hash: this.planHash,
            course: {
              code: this.course.code,
              name: this.course.name
            }
          }
        })
    },
    exportExcel() {
      // Analytics event
      if (this.$gtm.enabled())
        this.$gtm.trackEvent({
          event: 'site interaction',
          category: 'Course Plan and Fees',
          action: 'click',
          label: 'Excel Export',
          value: true,
          rest: {
            hash: this.planHash,
            course: {
              code: this.course.code,
              name: this.course.name
            }
          }
        })

      // Selected and assigned units
      const headings = [['Year', 'Year Number', 'Teaching Period', 'Unit Code', 'Unit Title', 'Unit Credit Points', 'Unit Offerings']]
      const selectedUnits = this.selectedItems.map((x) => x.relationship.unit).filter((y) => !!y)

      let tPeriods = headings
      const planYears = this.plan.years.forEach((year, yIndex) => {
        year.teachingPeriods.forEach((period, pIndex) => {
          period.units.forEach((x) => {
            const thisUnit = selectedUnits.filter((y) => y.code == x.code)[0]
            tPeriods.push([
              this.startYear + yIndex,
              'Year ' + (parseInt(yIndex) + 1) + ' ',
              period.teachingScheduled,
              x.code,
              thisUnit.name,
              x.creditPoints,
              x.defaultOfferings.join(', ')
            ])
          })
        })
      })

      let unitData = [['Course Plan'], [], ['Duration', this.plan.years.length == 1 ? '1 year' : this.plan.years.length + ' years'], []]

      unitData = unitData.concat(tPeriods)

      // Completed Units
      const completedPoints = this.completedTotalCredits
      if (completedPoints > 0) {
        unitData = unitData.concat([[], ['Units you have marked as completed'], [' ', ' ', ' ', 'Unit Code', 'Unit Title', 'Unit Credit Points']])

        const completedUnits = this.completedSelectedItems
          // Filter out duplicates based on unit code
          .filter((item, index, self) => self.findIndex(t => t.code === item.code) === index)
          .map((x) => {
          return [' ', ' ', ' ', x.relationship.unit.code, x.relationship.unit.name, x.relationship.unit.credits]
        })
        unitData = unitData.concat(completedUnits)
        unitData = unitData.concat([[], ['Total Completed Credits', ' ', ' ', ' ', ' ', completedPoints]])
      }

      // unallocated Units
      if (this.unallocatedUnits.length > 0) {
        unitData = unitData.concat([[], [], ['Unallocated Units'], [' ', ' ', ' ', 'Unit Code', 'Unit Title', 'Unit Credit Points']])
        let unallocated = this.unallocatedUnits.map((x) => {
          return [' ', ' ', ' ', x.relationship.unit.code, x.relationship.unit.name, x.relationship.unit.credits]
        })
        unitData = unitData.concat(unallocated)
      }

      // Write out to file
      let unitSheet = XLSX.utils.aoa_to_sheet(unitData)

      let wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, unitSheet, 'Course Plan Units')

      const fileName = 'Murdoch Course Visualizer Plan.xls'
      XLSX.writeFile(wb, fileName)
    },
    getCourseAcademicChairs: async function () {
      const course = this.course
      let courseAcademicChairs = this.course?.academicChair
      if (courseAcademicChairs == null || courseAcademicChairs == undefined || courseAcademicChairs.length < 1) {
        try {
          const majorsList = this.selectedItems.filter((x) => x.firstLevelTitle == 'Major' && x.relationship.type == 'Major').map((x) => x.relationship.id)
          const majorsPromises = majorsList.map(async (x) => await Curriculum.getMajorDetail(x))
          const majors = await Promise.all(majorsPromises)
          courseAcademicChairs = majors
            .map((x, xIndex) => {
              return x.academicChairs.map((y, yIndex) => {
                return {
                  index: xIndex + '_' + yIndex,
                  name: y.name,
                  email: y.email,
                  staffId: y.staffId,
                  majorId: x.id,
                  majorCode: x.code,
                  owningCollege: x.owningCollege,
                  title: x.title,
                  type: x.type
                }
              })
            })
            .flat()
        } catch (err) {
          console.log(JSON.stringify(err))
          this.error = err
        }
      }
      return courseAcademicChairs
    },
    // Button clicks will have an event "e". Mounted trigger will be null.
    async Generate(e) {
      let start = new Date()
      this.loading = true
      this.error = null
      try {
        await this.generateCoursePlan({ doForceGenerate: true, throwErrorOnNewErrors: false })
      } catch (err) {
        this.error = err
      }
      this.componentKey += 1
      this.loading = false

      let genTime = new Date() - start
      // Analytics event for plan generation
      if (this.$gtm.enabled())
        this.$gtm.trackEvent({
          event: 'site interaction',
          category: 'Course Plan and Fees',
          action: e ? 'click' : 'load',
          label: 'Plan Generation',
          value: true,
          rest: {
            generationTime: genTime,
            hash: this.planHash,
            unallocatedUnits: this.unallocatedUnits.length,
            course: {
              code: this.course.code,
              name: this.course.name
            }
          }
        })
    }
  },
  async mounted() {
    this.academicChair = await this.getCourseAcademicChairs()
    await this.Generate()
  }
}
</script>

<style></style>
